/**
 * #110 — drive the store's `toastVisible` flag off the LIVE sonner toast DOM.
 *
 * The app fires sonner toasts from dozens of scattered call sites (success /
 * error / promise toasts across editor, admin, auth, settings, …). Rather than
 * wrap every `toast(...)` call, we observe what sonner actually renders: it
 * portals a `[data-sonner-toaster]` container into the DOM and marks each live
 * toast with `[data-sonner-toast]`, flipping `data-removed="true"` during the
 * exit animation before unmounting. Counting non-removed toasts therefore gives
 * a reliable, self-healing "is a toast visible" signal — it can NEVER get stuck
 * "on", because it always reflects the current DOM (when the last toast leaves,
 * the count returns to zero and the flag clears).
 *
 * Mounted once near the app root. SSR/test-safe: no-ops without a `document`.
 */
import { useEffect } from 'react';
import { useStore } from '../store';

/** Count sonner toasts that are present and NOT mid-removal. */
function countVisibleToasts(): number {
  if (typeof document === 'undefined') return 0;
  const toasts = document.querySelectorAll('[data-sonner-toast]');
  let visible = 0;
  toasts.forEach((el) => {
    // Sonner sets data-removed="true" on a toast while it animates out.
    if (el.getAttribute('data-removed') !== 'true') visible += 1;
  });
  return visible;
}

export function useToastVisibility(): void {
  const setToastVisible = useStore((s) => s.setToastVisible);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    const sync = () => setToastVisible(countVisibleToasts() > 0);

    // Sonner portals the toaster into <body>; observe the whole subtree for
    // toast add/remove and the data-removed attribute flip on exit.
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-removed'],
    });

    // Initial sync in case a toast is already on screen at mount.
    sync();

    return () => {
      observer.disconnect();
      // Never leave the flag stuck on after unmount.
      setToastVisible(false);
    };
  }, [setToastVisible]);
}
